
<script>
import { onMount } from 'svelte';
import Button, { Label } from '@smui/button';
import Dialog, { Title, Content, Actions } from '@smui/dialog';
import IconButton from '@smui/icon-button';
import SectionIntro from './components/SectionIntro.svelte';
import SectionContext from './components/SectionContext.svelte';
import SectionTotalTripsInfo from './components/SectionTotalTripsInfo.svelte';
import SectionStationsInfo from './components/SectionStationsInfo.svelte';
import SectionTotals from './components/SectionTotals.svelte';
import SectionCharts from './components/SectionCharts.svelte';
import SectionTimeRange from './components/SectionTimeRange.svelte';
import SectionLocationMaps from './components/SectionLocationMaps.svelte';
import SectionConclusion from './components/SectionConclusion.svelte';
import SectionContactSteer from './components/SectionContactSteer.svelte';
import { ioRevealInit, ioSectionsInit, monthRefs, num1K1MParts } from '../src/common.js';
import { dataProviderSettings, DATA_START_MONTH, DATA_END_MONTH } from '../settings'
import metricsData from '../public/data/metrics.json';
import stationLocationsData from '../public/data/station-locations.json';

const PROVIDERS = Object.keys(metricsData.totals_annual).map(x => ({ ...dataProviderSettings.find(y => y.id === x.split("_").join("-")), id: x }))
console.log("PROVIDERS", PROVIDERS)
console.log("metricsData ", metricsData )
console.log("stationLocationsData ", stationLocationsData )

// remove app loading notice
document.getElementById('app-loader').style.display = 'none';

let appContainer;
let introTarget;
let sectionInView = { target: introTarget }
let chartDatasOpts = {
			fill: false,
			lineTension: 0.3,
			borderCapStyle: "butt",
			borderDash: [],
			borderDashOffset: 0.0,
			borderJoinStyle: "miter",
			pointBackgroundColor: "rgb(255, 255, 255)",
			pointBorderWidth: 3,
			pointHoverRadius: 1,
			pointHoverBackgroundColor: "rgb(0, 0, 0)",
			pointHoverBorderColor: "rgba(220, 220, 220, 1)",
			pointHoverBorderWidth: 1,
			pointRadius: 0,
			pointHitRadius: 10
}

const handleKeydown = (event) => {
  console.log("handleKeydown -> handleKeydown", event)
	if (event.keyCode === 32) {
		event.preventDefault();
		gotoNextSection()
	}
}

const gotoNextSection = () => {
	if (!sectionInView.target) {
		return
	}
	sectionInView.target.scrollIntoView({ behavior: 'smooth' });
  // console.log("gotoNextSection -> sectionInView.target", sectionInView.target)
}

onMount(async () => {
	ioSectionsInit(sectionInView);
	ioRevealInit(appContainer);
});
</script>


<svelte:window on:keydown={handleKeydown}/>

<main bind:this="{appContainer}">
		<img src="https://uk.steergroup.com/themes/custom/steer/logo.png" alt="Steer" class="app-logo svelte-fgxr05">
	<SectionIntro bind:this="{introTarget}"></SectionIntro>
	<SectionContext></SectionContext>
	<SectionTotals
		id="section-total-trips"
		title="Total Annual Trips"
		decPlaces="1"
		popupInfo="<div style='text-align:left'>Overall, people have been travelling less in all 6 cities during 2020, partly explained by stay-at-home orders and limited business operations.<br><br>
		More recently, some cities like Boston and Chicago have experienced a ramp up as cities started to reopen gradually.
		However, as of March 2021, cities like DC or LA are still struggling to regain the same ridership as 2019.</div>"
		data="{
			PROVIDERS.map(p => {
				const res = { ...p }
				res.data2019 = metricsData.totals_annual[p.id].results
												.filter(x => x.yr === 2019)
												.map(x => x.total_trips);
				res.data2019janmar = metricsData.totals_weekly[p.id].results
												.filter(x => x.yr === 2019 && x.week_no <= 13)
												.reduce((total, x) => Number(total) + Number(x.total_trips), 0);
				res.data2020 = metricsData.totals_annual[p.id].results
												.filter(x => x.yr === 2020)
												.map(x => x.total_trips);
				res.data2021 = metricsData.totals_annual[p.id].results
												.filter(x => x.yr === 2021)
												.map(x => x.total_trips);
				res.data2019 = res.data2019.length ? res.data2019[0] : 0
				res.data2020 = res.data2020.length ? res.data2020[0] : 0
				res.data2021 = res.data2021.length ? res.data2021[0] : 0
				res.numparts2019 = num1K1MParts(res.data2019, 0, 1)
				res.numparts2020 = num1K1MParts(res.data2020, 0, 1)
				res.numparts2021 = num1K1MParts(res.data2021, 0, 1)
				res.percentDiff = 100 - Math.floor((res.data2020 / res.data2019) * 100)
				res.percentDiff2021 = 100 - Math.floor((res.data2021 / res.data2019janmar) * 100)
				res.arrowUp = res.percentDiff < 0
				res.arrowUp2021 = res.percentDiff2021 < 0
				return res
			})
		}"></SectionTotals>
	<SectionCharts
		title="Seasonal Variation in Total Trips"
		subtitle="Total weekly cycle hire trips for each year by city"
		popupInfo="{
			{
				'bluebike_boston': { info: "Boston..." },
				'citibike_nyc': { info: `NYC saw a sharp drop in trip numbers from mid-March, near the start of the first Lockdown but saw a steady recovery from mid-April until mid-June where it is consistent with 2019 levels.` },
				'capitalbikeshare_dc': { info: "...." },
				'divvy_chicago': { info: "...." },
				'biketown_portland': { info: "...." },
				'bikeshare_la': { info: "...." }
			}
		}"
		chartOptions="{
			{
				tooltips: {
					callbacks: {
						title: function(tooltipItem, data) {
							return ""
						},
						label: function(tooltipItem, data) {
							let year = tooltipItem.datasetIndex === 0 ? 2019 : 2020
							let weekNo = parseInt(tooltipItem.label) + 1
							let monthMatch = monthRefs.find((x,i) => {
								return (weekNo * 7) <= x.dayscum && (weekNo * 7) > (monthRefs[i].dayscum - monthRefs[i].days)
							})
							return " " + parseInt(tooltipItem.value).toLocaleString('en') + " trips (" + (monthMatch ? monthMatch.name + "," : "-") + " week " + weekNo + " of " + year + ")"
						}
					}
				},
				annotation: {
					annotations: {
						box1: {
							type: 'box',
							xMin: 1,
							xMax: 2,
							yMin: 50,
							yMax: 70,
							backgroundColor: 'rgba(255, 99, 132, 0.25)'
						}
					}
				}
			}
		}"
		chartData="{
			PROVIDERS.map(p => {
				const res = { ...p }
				res.data = {
					labels2: ["January", "February", "March", "April", "May", "June", "July"],
					labels: Array.from(Array(metricsData.totals_weekly[p.id].results.filter(x => x.yr === 2019).length).keys()),
					datasets: [
						{
							...chartDatasOpts,
							...{
								label: "2019",
								order: 2,
								backgroundColor: "rgba(225, 204,230, .3)",
								borderColor: "#0492CF",
								pointBorderColor: "rgb(205, 130,1 58)",
								data: metricsData.totals_weekly[p.id].results.filter(x => x.yr === 2019).map(x => x.total_trips)
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2020",
								order: 1,
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#e5ff00",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.totals_weekly[p.id].results.filter(x => x.yr === 2020).map(x => x.total_trips)
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2021",
								order: 0,
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#ff9900",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.totals_weekly[p.id].results.filter(x => x.yr === 2021).map(x => x.total_trips)
							},
						}
					]
				}
				return res
			})
		}"></SectionCharts>

	<!-- <SectionTotalTripsInfo></SectionTotalTripsInfo> -->
	<SectionTotals
		title="Average Trip Duration"
		subtitle="Average cycle hire trip duration for each year by city"
		subnote="Excludes trip over 2 hours"
		mdIcon="schedule"
		units="min"
		popupInfo="<div style='text-align:left'>Overall, trip duration increased in most of the cities during 2020, from 28% in Chicago up to 91% in DC. In the case of LA, DC and NYC those trends have continued into early 2021.</div>"
		data="{
			PROVIDERS.map(p => {
				const res = { ...p }
				res.data2019 = metricsData.avg_duration_annual[p.id].results
											.filter(x => x.yr === 2019)
											.map(x => x.avg_duration/60);
				res.data2020 = metricsData.avg_duration_annual[p.id].results
											.filter(x => x.yr === 2020)
											.map(x => x.avg_duration/60);
				res.data2021 = metricsData.avg_duration_annual[p.id].results
											.filter(x => x.yr === 2021)
											.map(x => x.avg_duration/60);
				res.data2019 = res.data2019.length ? res.data2019[0] : 0
				res.data2020 = res.data2020.length ? res.data2020[0] : 0
				res.data2021 = res.data2021.length ? res.data2021[0] : 0
				res.numparts2019 = num1K1MParts(res.data2019, 0, 0)
				res.numparts2020 = num1K1MParts(res.data2020, 0, 0)
				res.numparts2021 = num1K1MParts(res.data2021, 0, 0)
				res.percentDiff = 100 - Math.floor((res.data2020 / res.data2019) * 100)
				res.percentDiff2021 = 100 - Math.floor((res.data2021 / res.data2019) * 100)
				res.arrowUp = res.percentDiff < 0
				res.arrowUp2021 = res.percentDiff2021 < 0
				return res
			})
		}"></SectionTotals>
	<SectionCharts
		title="Average Trip Duration"
		subtitle="Average cycle hire trip duration for each year by city."
		subnote="Excludes trip over 2 hours"
		mdIcon="schedule"
		popupInfo="{
			{
				'bluebike_boston': { info: "Boston..." },
				'citibike_nyc': { info: `NYC .......` },
				'capitalbikeshare_dc': { info: "...." },
				'divvy_chicago': { info: "...." },
				'biketown_portland': { info: "...." },
				'bikeshare_la': { info: "...." }
			}
		}"
		chartOptions="{
			{
				tooltips: {
					callbacks: {
						title: function(tooltipItem, data) {
							return ""
						},
						label: function(tooltipItem, data) {
							let year = tooltipItem.datasetIndex === 0 ? 2019 : 2020
							let weekNo = parseInt(tooltipItem.label) + 1
							let monthMatch = monthRefs.find((x,i) => {
								return (weekNo * 7) <= x.dayscum && (weekNo * 7) > (monthRefs[i].dayscum - monthRefs[i].days)
							})
							return " " + Math.round(tooltipItem.value/60) + " min (" + (monthMatch ? monthMatch.name + "," : "-") + " week " + weekNo + " of " + year + ")"
						}
					}
				},
			}
		}"
		chartData="{
			PROVIDERS.map(p => {
				const res = { ...p }
				res.data = {
					labels: Array.from(Array(metricsData.avg_duration_weekly[p.id].results.filter(x => x.yr === 2019).length).keys()),
					datasets: [
						{
							...chartDatasOpts,
							...{
								label: "2019",
								backgroundColor: "rgba(225, 204,230, .3)",
								borderColor: "#18c7de",
								pointBorderColor: "rgb(205, 130,1 58)",
								data: metricsData.avg_duration_weekly[p.id].results
													.filter(x => x.yr === 2019)
													.filter(x => x.avg_duration < 100 * 60)
													.map(x => (p.id === "trips_bikeshare_la"  ? x.avg_duration * 60 : x.avg_duration))
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2020",
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#e5ff00",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.avg_duration_weekly[p.id].results
													.filter(x => x.yr === 2020)
													.filter(x => x.avg_duration < 100 * 60)
													.map(x => (p.id === "trips_bikeshare_la"  ? x.avg_duration * 60 : x.avg_duration))
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2021",
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#ff9900",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.avg_duration_weekly[p.id].results
													.filter(x => x.yr === 2021)
													.filter(x => x.avg_duration < 100 * 60)
													.map(x => (p.id === "trips_bikeshare_la"  ? x.avg_duration * 60 : x.avg_duration))
							},
						}
					]
				}
				return res;
			})
		}"></SectionCharts>

	<SectionCharts
		title="Trip Time Distribution"
		subtitle="Time distribution of cycle hire trips over the hours of the day."
		subnote="Solid lines are weekday, dashed lines are weekend"
		show2021="{ false }"
		showWeekdaysEnds="{ true }"
		mdIcon="schedule"
		popupInfo="{
			{
				'bluebike_boston': { info: "Boston..." },
				'citibike_nyc': { info: `NYC .......` },
				'capitalbikeshare_dc': { info: "...." },
				'divvy_chicago': { info: "...." },
				'biketown_portland': { info: "...." },
				'bikeshare_la': { info: "...." }
			}
		}"
		chartOptions="{
			{
				scales: {
					xAxes: [{
						gridLines: {
							drawBorder: false,
							display: false,
							color: "rgba(0, 0, 0, 0)",
						},
						ticks: {
							precision: 1,
							fontColor: "rgba(255, 255, 255, 0.6)",
							display: true //this will remove only the label
						}
					}],
					yAxes: [{
						gridLines: {
							drawBorder: false,
							display: false,
							color: "rgba(0, 0, 0, 0)",
						},
						ticks: {
							precision: 0,
							display: false //this will remove only the label
						}
					}]
				}
			}
		}"
		chartData="{
			PROVIDERS.map(p => {
				const res = { ...p }
				res.data = {
					labels: Array.from(Array(metricsData.time_ranges[p.id].results.filter(x => x.yr === 2019 && x.day_of_week === 'weekday').length).keys()),
					datasets: [
						{
							...chartDatasOpts,
							...{
								label: "2019 weekday",
								backgroundColor: "rgba(225, 204,230, .3)",
								borderColor: "#18c7de",
								pointBorderColor: "rgb(205, 130,1 58)",
								data: metricsData.time_ranges[p.id].results
													.filter(x => x.yr === 2019 && x.day_of_week === 'weekday')
													.map(x => x.trip_count)
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2020 weekday",
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#e5ff00",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.time_ranges[p.id].results
													.filter(x => x.yr === 2020 && x.day_of_week === 'weekday')
													.map(x => x.trip_count)
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2019 weekend",
								borderDash: [5,5],
								borderWidth: 1,
								backgroundColor: "rgba(225, 204,230, .3)",
								borderColor: "#18c7de",
								pointBorderColor: "rgb(205, 130,1 58)",
								data: metricsData.time_ranges[p.id].results
													.filter(x => x.yr === 2019 && x.day_of_week === 'weekend')
													.map(x => x.trip_count)
							},
						},
						{
							...chartDatasOpts,
							...{
								label: "2020 weekend",
								borderDash: [5,5],
								borderWidth: 1,
								backgroundColor: "rgba(184, 185, 210, .3)",
								borderColor: "#e5ff00",
								pointBorderColor: "rgb(35, 26, 136)",
								data: metricsData.time_ranges[p.id].results
													.filter(x => x.yr === 2020 && x.day_of_week === 'weekend')
													.map(x => x.trip_count)
							},
						},
					]
				}
				return res;
			})
		}"></SectionCharts>

	<SectionLocationMaps
		title="Popular Stations"
		popupInfo="{
			{
				'bluebike_boston': { info: "When comparing 2019 and 2020 top destinations, a dozen of new stations appeared in the list of most popular Blue Bikes station at proximity to major hospitals. The stations located close by Boston Children’s Hospital, Joslin Diabetes Center, Brigham & Women’s Hospital and Harvard Medical School were around the most popular during the first two waves of the pandemic. " },
				'citibike_nyc': { info: `The majority of stations visited between March 2019 and May 2020 were all located in Manhattan and Brooklyn. In 2020, the stations located near the Hudson River Greenway, the Lincoln Bridge, Hudson River Park and Battery Park City were the most popular, suggesting an increase in recreational trips. ` },
				'capitalbikeshare_dc': { info: "Destinations near pharmacies and grocery stores found their way onto the list of top Capital Bikeshare destinations during the 2020 time period" },
				'divvy_chicago': { info: "The Chicago Department of Transportation (CDOT) announced that August 2020 was the Divvy’s largest ridership month ever with 612,928 rides taken, a 3.9% increase compared to the previous year. CDOT associates most of these trips to leisure/recreational trips + system expansion" },
				'biketown_portland': { info: "...." },
				'bikeshare_la': { info: `For several reasons, L.A. County bike share systems haven’t stepped up to serve mobility needs quite as broadly as much larger systems in NYC, Chicago, Boston, and Washington D.C. have, including: Southern California streets remain largely hostile to cycling, with safe bikeways being few and far between. Bike share coverage is spotty to non-existent outside of Santa Monica, North Hollywood, Downtown Los Angeles, Central LA, Venice and the Westside. It’s also worth noting that the cities of Culver City, Long Beach run their own bike share systems not pictured here. The city of Santa Monica had their own bike share until the end of 2020. It was part of the Bike Share Connect system that included UCLA (Westwood), Beverly Hills, West Hollywood and Long Beach. Some of these services have paused during the pandemic. The Systems cater to all types riders (e.g. commuters and tourists) as well as essential (not to mention low-income) workers`}
			}
		}"
		stationLocations="{ stationLocationsData }"
		popularStations="{ metricsData.popular_start_stations }"></SectionLocationMaps>
	<SectionConclusion></SectionConclusion>
	<SectionContactSteer></SectionContactSteer>
</main>

<style>
/* Accessing the class with "*" in front limits
	the scope to anything under this component's
	elements. */
* :global(.myClass) {
	font-family: 'Roboto Mono', monospace !important;
}

.app-logo {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 90px;
    z-index: 1;
}
main {
	text-align: center;
	padding: 0;
	margin: 0 auto;
}

h1 {
	font-size: 54px;
	color: #fff;
}

@media (min-width: 768px) {
	main {
		max-width: none;
	}
}
</style>
